import React from 'react';

import type { FieldConfig } from '@/types/forms';

import { FormFieldRenderer } from './FormFieldRenderer';
import { FormFooter } from './FormFooter';
import { FormGrid } from './FormGrid';
import { FormSuccess } from './FormSuccess';

type FooterLink = {
  label: string;
  href: string;
};

type SecurityConfig = {
  honeypot?: boolean;
  honeypotName?: string;
};

export function areRequiredFieldsFilled(
  fields: FieldConfig[],
  values: Record<string, unknown> | undefined,
): boolean {
  return fields.every((field) => {
    if (!field.required) {
      return true;
    }

    const value = values?.[field.name];

    if (value === null) {
      return false;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  });
}

type DynamicFormContentProps = {
  success: boolean;
  control: any;
  errors: any;
  isSubmitting: boolean;
  watchedValues: any;
  submitError: string | null;
  handleFormSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  fields: FieldConfig[];
  columns: 1 | 2;
  submitLabel: string;
  footerLink?: FooterLink;
  buttonClassName?: string;
  buttonPaddingClassName?: string;
  buttonContainerClassName?: string;
  className?: string;
  renderFooterExtra?: (values: any) => React.ReactNode;
  disableSubmitUntilFilled?: boolean;
  security?: SecurityConfig;
  honeypotRef?: React.RefObject<HTMLInputElement | null>;
};

export function DynamicFormContent({
  success,
  control,
  errors,
  isSubmitting,
  watchedValues,
  submitError,
  handleFormSubmit,
  fields,
  columns,
  submitLabel,
  footerLink,
  buttonClassName,
  buttonPaddingClassName,
  buttonContainerClassName,
  className = '',
  renderFooterExtra,
  disableSubmitUntilFilled = false,
  security,
  honeypotRef,
}: DynamicFormContentProps) {
  if (success) {
    return <FormSuccess />;
  }

  return (
    <form
      onSubmit={handleFormSubmit}
      noValidate
      className={`flex flex-col gap-4 ${className}`}
    >
      {security?.honeypot && (
        <input
          ref={honeypotRef}
          type="text"
          name={security.honeypotName ?? 'lips-name'}
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute left-[-9999px] h-px w-px overflow-hidden opacity-0"
        />
      )}

      <FormGrid columns={columns}>
        {fields.map((field) => (
          <FormFieldRenderer
            key={field.name}
            field={field}
            control={control}
            errors={errors}
          />
        ))}
      </FormGrid>

      <FormFooter
        isSubmitting={isSubmitting}
        submitLabel={submitLabel}
        buttonClassName={buttonClassName}
        buttonPaddingClassName={buttonPaddingClassName}
        buttonContainerClassName={buttonContainerClassName}
        footerLink={footerLink}
        submitError={submitError}
        disabled={
          isSubmitting ||
          (disableSubmitUntilFilled &&
            !areRequiredFieldsFilled(fields, watchedValues))
        }
      >
        {renderFooterExtra?.(watchedValues)}
      </FormFooter>
    </form>
  );
}
